import type { NotificationSettingsDto } from '@edms/shared';
import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * Push-уведомления и установка приложения. Сообщения показывает service worker (public/sw.js):
 * на телефоне — в шторке, как от мессенджеров, на компьютере — в центре уведомлений системы.
 */

export type PushState =
  /** Браузер не умеет push. */
  | 'unsupported'
  /** iPhone/iPad: уведомления только в установленном приложении («На экран Домой»). */
  | 'ios-install'
  /** Уведомления запрещены в настройках браузера. */
  | 'denied'
  | 'off'
  | 'on';

export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** Открыто как установленное приложение (а не во вкладке браузера). */
export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration('/')) ?? null;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  return (await (await registration())?.pushManager.getSubscription()) ?? null;
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.permission === 'granted' && (await currentSubscription()) ? 'on' : 'off';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const raw = atob(base64url.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Включить уведомления на этом устройстве: разрешение браузера → подписка → сервер. */
export async function enablePush(): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'Уведомления запрещены в браузере' : 'Разрешение не дано');
  const reg = (await registration()) ?? (await navigator.serviceWorker.register('/sw.js'));
  await navigator.serviceWorker.ready;
  const { publicKey } = await api.get<NotificationSettingsDto>('/notifications/settings');
  let sub = await reg.pushManager.getSubscription();
  // Подписка от старого ключа (сервер переустановили) не примется службой push — переподписываемся.
  const key = sub?.options.applicationServerKey;
  if (sub && key && btoa(String.fromCharCode(...new Uint8Array(key))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') !== publicKey) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await api.post('/notifications/push/subscribe', sub.toJSON());
}

/** Выключить на этом устройстве; при выходе из системы — тоже, чтобы чужие уведомления сюда не шли. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.post('/notifications/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/**
 * После входа: если браузер уже подписан, напомнить серверу, чей это телефон
 * (например, сервер удалил подписку или устройство перешло к другому сотруднику).
 */
export async function syncPush(): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return;
  const sub = await currentSubscription();
  if (sub) await api.post('/notifications/push/subscribe', sub.toJSON()).catch(() => undefined);
}

/** Число непрочитанных на иконке установленного приложения. */
export function setAppBadge(count: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (!nav.setAppBadge) return;
  void (count > 0 ? nav.setAppBadge(count) : nav.clearAppBadge?.())?.catch(() => undefined);
}

/** Сообщения от service worker: пришло уведомление — обновить; нажали на него — открыть страницу. */
export function onServiceWorkerMessage(handlers: { notification: () => void; open: (url: string) => void }): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const listener = (e: MessageEvent<{ type?: string; url?: string }>) => {
    if (e.data?.type === 'edms:notification') handlers.notification();
    if (e.data?.type === 'edms:open' && e.data.url) handlers.open(e.data.url);
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}

// ---------------------------------------------------------------------------
// Установка приложения (Chrome, Edge, Яндекс на Android и компьютере)
// ---------------------------------------------------------------------------

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  // Браузер предлагает установку один раз за загрузку — событие запоминаем до нажатия кнопки.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

/** Можно ли сейчас показать кнопку «Установить приложение» и что она делает. */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<boolean> } {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = () => rerender((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return {
    canInstall: deferred !== null,
    install: async () => {
      if (!deferred) return false;
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      listeners.forEach((l) => l());
      return outcome === 'accepted';
    },
  };
}
